import React, { useState, useEffect, useRef } from 'react';
import DogImageGallery from '../ImageGallery';
import { loadPuppyGalleryImages } from '../../utils/photoUtils';
import { trackEvent } from '../../utils/matomoEvents';

const PuppyGalleryModal = ({ isOpen, onClose, puppy, disableNavigation }) => {
  const [images, setImages] = useState([]); // Array of images for the gallery
  const [loading, setLoading] = useState(true); // Loading state for the gallery
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen state
  const openTimeRef = useRef(null); // Track when modal was opened

  // Track modal open event
  useEffect(() => {
    if (isOpen && puppy) {
      openTimeRef.current = Date.now();
      trackEvent('PuppyGallery', 'ModalOpen', `${puppy.name} - ${puppy.status}`);
    }
  }, [isOpen, puppy]);

  // Load puppy images when the modal is opened
  useEffect(() => {
    if (isOpen && puppy) {
      setLoading(true);
      const loadImages = async () => {
        try {
          const puppyImages = await loadPuppyGalleryImages(puppy);
          setImages(puppyImages);
        } catch (error) {
          console.error('Error loading puppy images:', error);
          setImages([puppy.image]); // Fallback to profile image
        } finally {
          setLoading(false);
        }
      };
      loadImages();
    }
  }, [isOpen, puppy]);

  // Handle fullscreen toggle
  const handleFullscreenToggle = (fullscreen) => {
    setIsFullscreen(fullscreen);
  };

  // Handle modal close with tracking
  const handleClose = () => {
    if (openTimeRef.current && puppy) {
      const timeSpentSeconds = Math.round((Date.now() - openTimeRef.current) / 1000);
      trackEvent('PuppyGallery', 'ModalClose', `${puppy.name} - ${puppy.status}`, timeSpentSeconds);
    }
    onClose();
  };

  if (!isOpen) return null; // Do not render if the modal is not open

  const modalBackgroundColor = puppy.status === 'Unavailable' ? 'var(--unavailable-bg)' : 'var(--surface)';
  const modalTextColor = puppy.status === 'Unavailable' ? 'var(--unavailable-text)' : 'var(--text)';
  const statusColor = puppy.status === 'Unavailable' ? 'var(--unavailable-status)' : 'inherit';

  // Close the modal when clicking outside the gallery
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('puppy-gallery-modal-overlay')) {
      handleClose();
    }
  };

  return (
    <div className="puppy-gallery-modal-overlay" onClick={handleOverlayClick}>
      <div className="puppy-gallery-modal" style={{ backgroundColor: modalBackgroundColor, color: modalTextColor }}>
        {!isFullscreen && (
          <button className="close-button" onClick={handleClose} style={{ color: modalTextColor }}>
            &times;
          </button>
        )}
        <h2>{puppy.name}</h2>
        <div className="puppy-info">
          <p>
            {/* Same as the puppy card: omit fields we have no value for. */}
            {puppy.gender && <><strong>Gender:</strong> {puppy.gender}<br /></>}
            {puppy.markings && <><strong>Markings:</strong> {puppy.markings}<br /></>}
            {puppy.status && <><strong>Status:</strong> <span style={{ color: statusColor }}>{puppy.status}</span><br /></>}
            {puppy.pet_price && <><strong>Price:</strong> {puppy.pet_price}</>}
          </p>
        </div>
        {loading ? (
          <div className="loading">Loading gallery...</div>
        ) : (
          <div className="puppy-gallery-container">
            <DogImageGallery
              images={images}
              showDogInfo={true}
              renderImageInfo={(imagePath) => {
                const filename = imagePath.split('/').pop();
                const parts = filename.split('_');
                if (parts.length >= 4) {
                  const photoNumber = parts[0];
                  const date = parts[1].replace('-', ' ');
                  const weeks = parts[2].replace(/[^0-9]/g, '');
                  const type = parts[3].startsWith('P') ? 'Profile' : 'Normal';
                  return (
                    <div className="photo-info">
                      <p>{`${date} - ${weeks} weeks old`}</p>
                    </div>
                  );
                }
                return null;
              }}
              onFullscreenToggle={handleFullscreenToggle}
              showNavigationButtons={!disableNavigation} // Disable navigation if specified
              showPlayButton={!disableNavigation} // Disable play button if specified
            />
          </div>
        )}
      </div>
      <style jsx>{`
        .puppy-gallery-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: var(--modal-scrim);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .puppy-gallery-modal {
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 2rem;
          position: relative;
        }

        .close-button {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 2rem;
          cursor: pointer;
        }

        .puppy-info {
          margin-bottom: 1.5rem;
        }

        .puppy-gallery-container {
          width: 100%;
        }

        .loading {
          text-align: center;
          padding: 2rem;
          font-style: italic;
          color: var(--text-faint);
        }

        .photo-info {
          text-align: center;
          margin-top: 1rem;
          font-size: 0.9rem;
          font-weight: bold;
          color: ${puppy.status === 'Unavailable' ? 'var(--unavailable-text)' : 'var(--text-faint)'};
        }
      `}</style>
    </div>
  );
};

export default PuppyGalleryModal;